import { Component, OnDestroy, OnInit } from '@angular/core';
import { NxCheckboxChangeEvent } from '@aposin/ng-aquila/checkbox';
import { Store } from '@ngrx/store';
import { Observable, Subject, map, takeUntil } from 'rxjs';
import { AGREEMENT_TYPE } from '../../model/agreements.model';
import { ApiState } from '../../model/api-state.model';
import { FormsService } from '../../services/forms.service';
import { PersistStorageKeys, PersistStorageService } from '../../services/persist-storage.service';
import { ProcessService } from '../../services/process.service';
import { AppState } from '../../store';
import * as fromAgreements from '../../store/agreements/agreements.reducer';
import * as fromProcess from '../../store/process/process.reducer';
import { isEmpty } from '../../utils';
import isAppAgentOrTravelAgency from '../../utils/is-agent-or-travel-agency.util';
import { GtmService } from '../../services/gtm.service';

const DISPLAY_SELECT_ALL_CHECKBOX_MIN_LENGTH = 2;

@Component({
  selector: 'app-step6',
  templateUrl: './step6.component.html',
})
export class Step6Component implements OnInit, OnDestroy {
  agreementsState$ = this.store.select(fromAgreements.selectAgreementsState);
  marketingAgreements$ = this.store.select(fromAgreements.selectMarketingAgreementsData);
  marketingAgreementsIsEmpty$ = this.store.select(fromAgreements.selectMarketingAgreementsData).pipe(map(isEmpty));
  statementsAgreements$ = this.store.select(fromAgreements.selectStatementsAgreementsData);
  statementsAgreementsError$ = this.store.select(fromAgreements.selectStatementsAgreementsError);
  statementsAgreementsIsEmpty$ = this.store.select(fromAgreements.selectStatementsAgreementsData).pipe(map(isEmpty));
  agreementsAgreements$ = this.store.select(fromAgreements.selectStatementsAgreementsData);
  agreementsAgreementsIsEmpty$ = this.store.select(fromAgreements.selectStatementsAgreementsData).pipe(map(isEmpty));
  isAppAgentOrTravelAgency = isAppAgentOrTravelAgency();

  selectedAllMarketing$ = this.formsService.agreementsForm.controls.marketing.valueChanges.pipe(
    map((value) => Object.values(value).every((v) => v)),
  );
  selectedAllStatements$ = this.formsService.agreementsForm.controls.marketing.valueChanges.pipe(
    map((value) => Object.values(value).every((v) => v)),
  );

  ngDestroy$ = new Subject<void>();

  formGroup = this.formsService.agreementsForm;

  protected readonly apiState = ApiState;
  protected readonly agreementType = AGREEMENT_TYPE;

  constructor(
    private store: Store<AppState>,
    private formsService: FormsService,
    private processService: ProcessService,
    private persistStore: PersistStorageService,
    private gtmService: GtmService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(fromProcess.actions.setStep({ step: 5 }));
    this.gtmService.step6Event();
    this.processService.onPageInit();

    this.store.dispatch(fromAgreements.actions.fetchStart());

    const agreementsValues = this.persistStore.getJson<{ marketing: Record<string, boolean>; statements: Record<string, boolean> }>(
      PersistStorageKeys.FormsAgreements,
    );

    this.marketingAgreements$.pipe(takeUntil(this.ngDestroy$)).subscribe((agreements) => {
      this.formsService.setAgreementsForm(agreements || [], AGREEMENT_TYPE.MARKETING, agreementsValues?.marketing);
    });

    this.statementsAgreements$.pipe(takeUntil(this.ngDestroy$)).subscribe((agreements) => {
      this.formsService.setAgreementsForm(agreements || [], AGREEMENT_TYPE.STATEMENTS, agreementsValues?.statements);
    });
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  getLabel(index: number, type: AGREEMENT_TYPE): Observable<string> {
    switch (type) {
      case AGREEMENT_TYPE.MARKETING:
        return this.marketingAgreements$.pipe(map((response) => (response ? response[index]?.agreementText : '')));
      case AGREEMENT_TYPE.STATEMENTS:
        return this.statementsAgreements$.pipe(map((response) => (response ? response[index]?.agreementText : '')));
    }
  }

  selectAllVisible$(type: AGREEMENT_TYPE): Observable<boolean> {
    switch (type) {
      case AGREEMENT_TYPE.MARKETING:
        return this.marketingAgreements$.pipe(
          takeUntil(this.ngDestroy$),
          map((response) => (response ? response.length >= DISPLAY_SELECT_ALL_CHECKBOX_MIN_LENGTH : false)),
        );
      case AGREEMENT_TYPE.STATEMENTS:
        return this.statementsAgreements$.pipe(
          takeUntil(this.ngDestroy$),
          map((response) => (response ? response.length >= DISPLAY_SELECT_ALL_CHECKBOX_MIN_LENGTH : false)),
        );
    }
  }

  handleChangeSelectAll(event: NxCheckboxChangeEvent, type: AGREEMENT_TYPE): void {
    switch (type) {
      case AGREEMENT_TYPE.MARKETING:
        this.formsService.agreementFormAllMarketingValue(event.checked);
        break;
      case AGREEMENT_TYPE.STATEMENTS:
        this.formsService.agreementFormAllStatementsValue(event.checked);

        break;
    }
  }
}
